import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Send, Zap, CalendarClock, MessageSquare } from "lucide-react";
import { Wizard, WField, WNote } from "@/components/fs/Wizard";
import { WhoDoesIt, whoBlocked, performWho, useCommunityVendors, ownerFor, CAT_LABEL } from "@/pages/dashboard/fs/WhoDoesIt";
import { heldToast } from "@/components/fs/SendWindowNote";
import { ScopeEditor } from "@/components/fs/ScopeEditor";
import { V } from "@/lib/vocab";

/* Wizard 6 — raise a job by hand, the fourth way a work order is created. Usually someone
   ringing the office. The one question that matters is urgency, so it gets its own step with
   the consequence spelled out rather than being a pill nobody reads. */
export default function NewJobWizard({ tenantId, onDone, onClose }) {
  const [f, setF] = useState({
    category: "common_area_cleaning", summary: "", location: "",
    priority: "normal", reporter_name: "", reporter_phone: "", scope: "",
  });
  // iter372 — the scope travels with the job: to the company and to the CC list (switches), never to residents.
  const [share, setShare] = useState({ vendor: true, cc: true });
  const [scopeFiles, setScopeFiles] = useState([]);
  // iter370 — "Who's doing it" is part of opening a job, not a separate flow.
  const vendors = useCommunityVendors(tenantId);
  const [who, setWho] = useState({ mode: "", vendor_id: "", invite: {}, quote_ids: [] });
  useEffect(() => {
    if (!vendors || who.mode) return;
    const owner = ownerFor(vendors, f.category);
    setWho(owner ? { mode: "existing", vendor_id: owner.id, invite: {}, quote_ids: [] }
      : { mode: (vendors.some((v) => v.link && v.link.status !== "removed") ? "existing" : "invite"), vendor_id: "", invite: {}, quote_ids: [] });
  }, [vendors, f.category]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const owner = ownerFor(vendors, f.category);
      const autoRoute = who.mode === "existing" && owner && who.vendor_id === owner.id;
      const { data } = await api.post(`/tenants/${tenantId}/field-services/work-orders`, { ...f, scope_share: share, hold: !autoRoute });
      if (data.work_order_id && scopeFiles.length && !data.duplicate) {
        for (const file of scopeFiles) {
          const fd = new FormData(); fd.append("file", file);
          try { await api.post(`/tenants/${tenantId}/field-services/wos/${data.work_order_id}/scope-file`, fd); }
          catch (e) { toast.error(`${file.name}: ${errMsg(e, "didn't attach")}`); }
        }
      }
      if (data.duplicate) {
        toast.success(`Someone already reported that — folded into ${data.wo_ref}`);
      } else if (autoRoute) {
        toast.success(data.blocked ? `${data.wo_ref} is ${owner.name}'s — nobody is sent out until their paperwork clears`
          : data.routed_via === "direct" ? `${data.wo_ref} sent straight to ${owner.name}'s crew on site`
            : `${data.wo_ref} sent to ${owner.name}`);
      } else if (who.mode === "later" || !data.work_order_id) {
        toast.success(`${data.wo_ref} logged — it's under Work orders marked "no company yet"`);
      } else {
        const res = await performWho(tenantId, data.work_order_id, who);
        const vn = res?.work_order?.vendor_name;
        toast.success(who.mode === "invite" ? `${data.wo_ref} logged — ${heldToast(res?.held, `${vn || "the company"}'s invite`) || `${vn || "the company"} has been invited and gets the job when their paperwork clears`}`
          : who.mode === "quotes" ? `${data.wo_ref} logged — ${who.quote_ids.length} compan${who.quote_ids.length === 1 ? "y" : "ies"} asked to price it`
            : `${data.wo_ref} sent to ${vn || "the company"}`);
      }
      onDone?.(); onClose?.();
    } catch (e) { toast.error(errMsg(e, "Could not open the job")); }
    setBusy(false);
  };

  const steps = [
    {
      key: "what", title: "What & where",
      hint: "Write it the way the person on site will read it on their phone.",
      blocked: !f.summary.trim() ? "Say what needs doing."
        : !f.location.trim() ? "Say where it is — the crew has to find it." : null,
      render: () => (
        <>
          <WField label="What needs doing">
            <input data-testid="new-job-summary" value={f.summary} autoFocus
              placeholder="Bin overflowing by the mailboxes"
              onChange={(e) => setF({ ...f, summary: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          </WField>
          <WField label="Where exactly">
            <input data-testid="new-job-location" value={f.location}
              placeholder="Mailbox kiosk, by Unit 12"
              onChange={(e) => setF({ ...f, location: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          </WField>
        </>
      ),
    },
    {
      key: "trade", title: "What kind of work",
      hint: "This decides who it goes to. If a company owns this trade here, it goes straight to their crew — no office in the middle.",
      render: () => (
        <div className="flex flex-wrap gap-1.5" data-testid="new-job-cats">
          {Object.entries(CAT_LABEL).map(([k, label]) => (
            <button key={k} type="button" data-testid={`new-job-cat-${k}`} onClick={() => setF({ ...f, category: k })}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${f.category === k ? "bg-redline-500 text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>
              {label}
            </button>
          ))}
        </div>
      ),
    },
    {
      key: "scope", title: "Scope of work",
      hint: "Optional, but it's what stops the surprise invoice. Write it, dictate it, let AI draft it from the title, or attach the PDF you already have.",
      render: () => (
        <ScopeEditor tenantId={tenantId} kind="job" value={f.scope} onChange={(s) => setF({ ...f, scope: s })}
          draftCtx={{ title: f.summary, category: f.category, location: f.location, priority: f.priority }}
          pending={scopeFiles} onPending={setScopeFiles} share={share} onShare={setShare} testid="new-job-scope" />
      ),
    },
    {
      key: "urgency", title: "How urgent",
      hint: "This isn't a label — it changes what actually happens next.",
      render: () => (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="new-job-urgency">
          <button type="button" data-testid="new-job-priority-normal" onClick={() => setF({ ...f, priority: "normal" })}
            className={`rounded-xl border p-3.5 text-left transition-colors ${f.priority === "normal" ? "border-ink bg-sand-50" : "border-sand-200 bg-white hover:border-sand-300"}`}>
            <span className="flex items-center gap-2 text-sm font-bold text-ink">
              <CalendarClock className="h-4 w-4 text-ink/45" /> Normal
            </span>
            <span className="mt-1 block text-xs leading-relaxed text-ink/55">
              Goes on the crew's list for their next scheduled visit. Nobody gets pulled out for it.
            </span>
          </button>
          <button type="button" data-testid="new-job-priority-urgent" onClick={() => setF({ ...f, priority: "urgent" })}
            className={`rounded-xl border p-3.5 text-left transition-colors ${f.priority === "urgent" ? "border-danger-400 bg-danger-50/60" : "border-sand-200 bg-white hover:border-sand-300"}`}>
            <span className="flex items-center gap-2 text-sm font-bold text-ink">
              <Zap className="h-4 w-4 text-danger-500" /> Urgent
            </span>
            <span className="mt-1 block text-xs leading-relaxed text-ink/55">
              Someone comes out today. Skips the visit queue entirely and starts the escalation
              clock — their office, then you, if nobody answers.
            </span>
          </button>
        </div>
      ),
    },
    {
      key: "doer", title: "Who's doing it",
      hint: "This used to be a different flow. Now it's just the next question.",
      blocked: whoBlocked(who),
      render: () => <WhoDoesIt tenantId={tenantId} category={f.category} vendors={vendors} choice={who} onChange={setWho} />,
    },
    {
      key: "who", title: "Who reported it",
      hint: "Optional, but it's the difference between them being told it's done and them ringing you again to ask.",
      render: () => (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <WField label="Name">
              <input data-testid="new-job-reporter-name" value={f.reporter_name} placeholder="Dana Whitfield"
                onChange={(e) => setF({ ...f, reporter_name: e.target.value })}
                className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none" />
            </WField>
            <WField label="Their mobile">
              <input data-testid="new-job-reporter-phone" value={f.reporter_phone} placeholder="+1 512 555 0134"
                onChange={(e) => setF({ ...f, reporter_phone: e.target.value })}
                className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none" />
            </WField>
          </div>
          <WNote testid="new-job-reporter-note">
            <MessageSquare className="mr-1 inline h-3.5 w-3.5" />
            With a mobile number they get "it's on the crew's list", then "it's done" with the crew's
            photo — and they can follow it in the {V.lc("person")} portal. Anyone who's replied STOP is skipped.
          </WNote>
        </>
      ),
    },
    {
      key: "review", title: "Review",
      render: () => (
        <>
          <div className="rounded-xl border border-sand-200 bg-white p-4" data-testid="new-job-review">
            <div className="font-heading text-base font-bold text-ink">{f.summary || "Untitled job"}</div>
            <div className="mt-1 text-xs text-ink/55">{f.location || "no location"} · {CAT_LABEL[f.category]}</div>
            <div className="mt-2 text-xs text-ink/60">
              {f.priority === "urgent" ? "Urgent — someone comes out today" : "Normal — on the next scheduled visit"}
              {f.reporter_name || f.reporter_phone
                ? ` · reported by ${f.reporter_name || `${V.a("person")}`}${f.reporter_phone ? " (they'll be kept posted)" : ""}`
                : " · no reporter recorded"}
            </div>
            <div className="mt-1 text-xs text-ink/60" data-testid="new-job-review-scope">
              {f.scope.trim() ? `${f.scope.trim().split(/\s+/).length} words of scope` : "No written scope"}
              {scopeFiles.length ? ` · ${scopeFiles.length} document${scopeFiles.length === 1 ? "" : "s"} attached` : ""}
              {(f.scope.trim() || scopeFiles.length) ? ` · goes to ${[share.vendor !== false && "the company", share.cc !== false && "the CC list"].filter(Boolean).join(" and ") || "nobody yet"}` : ""}
            </div>
            <div className="mt-2 text-xs font-semibold text-ink/75" data-testid="new-job-review-who">
              {who.mode === "existing" ? `Goes to ${(vendors || []).find((v) => v.id === who.vendor_id)?.name || "the company you picked"}`
                : who.mode === "invite" ? `${who.invite?.name || "The company"} will be invited and gets the job when their paperwork clears`
                  : who.mode === "quotes" ? `${who.quote_ids.length} compan${who.quote_ids.length === 1 ? "y" : "ies"} asked for a quote first`
                    : "No company yet — you'll pick one from Work orders"}
            </div>
          </div>
          <WNote tone="amber" testid="new-job-gate-note">
            Nobody is sent to the property until the company's insurance, W-9 and agreement are on file. If
            anything's missing you'll see exactly what, and they'll have been asked for it.
          </WNote>
        </>
      ),
    },
  ];

  return (
    <Wizard testid="wiz-job" steps={steps} busy={busy} onFinish={create}
      finishLabel="Open the job" finishIcon={Send} />
  );
}
